import React, { useRef } from 'react';
import { gsap } from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

const Hero: React.FC = () => {
  const container = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const bgRef = useRef<HTMLDivElement>(null);

  useGSAP(() => {
    const tl = gsap.timeline();

    // Text entrance animations
    tl.from(".hero-title", {
      y: 40,
      opacity: 0,
      duration: 1.1,
      ease: "power4.out"
    })
    .from(".studio-line", {
      scaleX: 0,
      opacity: 0,
      duration: 1.4,
      ease: "expo.out"
    }, "-=0.8")
    .from(".studio-text", {
      y: 8,
      opacity: 0,
      duration: 0.9,
      ease: "power2.out"
    }, "-=1")
    .from(".hero-tagline", {
      y: 24,
      opacity: 0,
      duration: 0.9,
      ease: "power3.out"
    }, "-=0.7");

    // Scroll parallax effect for the image background
    gsap.to(bgRef.current, {
      yPercent: 16,
      ease: "none",
      scrollTrigger: {
        trigger: container.current,
        start: "top top",
        end: "bottom top",
        scrub: true
      }
    });

    const handleMouseMove = (e: MouseEvent) => {
      const { clientX, clientY } = e;
      const xNorm = (clientX / window.innerWidth - 0.5);
      const yNorm = (clientY / window.innerHeight - 0.5);

      if (bgRef.current) {
        gsap.to(bgRef.current, {
          scale: 1.05 + (Math.abs(xNorm) * 0.03),
          x: xNorm * -10,
          y: yNorm * -10,
          duration: 3,
          ease: "power1.out"
        });
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, { scope: container });

  return (
    <section
      ref={container}
      className="hero-section h-dvh w-full relative bg-[#1A3C28] overflow-hidden perspective-1000"
    >
      {/* Image Background Layer (landscape = web, portrait = mobile) */}
      <div className="absolute inset-0 z-0 w-full h-full overflow-hidden">
        <picture>
          {/* Mobile / portrait view */}
          <source
            media="(orientation: portrait) and (max-width: 820px)"
            srcSet="/hero/hero-portrait.webp"
            type="image/webp"
          />
          <source
            media="(orientation: portrait) and (max-width: 820px)"
            srcSet="/hero/hero-portrait.jpg"
            type="image/jpeg"
          />
          {/* Desktop / landscape view (default) */}
          <source
            media="(min-width: 821px), (orientation: landscape)"
            srcSet="/hero/hero-landscape.webp"
            type="image/webp"
          />
          <source
            media="(min-width: 821px), (orientation: landscape)"
            srcSet="/hero/hero-landscape.jpg"
            type="image/jpeg"
          />
          <img
            ref={bgRef}
            src="/hero/hero-landscape.jpg"
            alt="GUSTO hero background"
            className="hero-bg-img w-full h-full object-cover scale-110 will-change-transform"
            loading="eager"
            decoding="async"
            fetchPriority="high"
          />
        </picture>
        {/* Subtle darkening so the brand text remains legible without burying the pizza */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/55 z-10 pointer-events-none"></div>
      </div>

      {/* Primary Brand Mark — pinned to the top of the hero, directly BELOW the navbar.
          NOTE: NO Tailwind `relative` / `absolute` class here — the .hero-brand-wrap
          rule in index.html sets position:absolute so the brand actually sticks to top. */}
      <div ref={textRef} className="hero-brand-wrap z-20 flex flex-col items-center w-full max-w-[95vw]">
        <h1 className="hero-title font-display font-black text-[#F2E9D8] uppercase leading-[0.78] tracking-tighter text-center hero-brand-title">
            GUS<span className="text-[#E05A38]">TO</span>
        </h1>

        <div className="flex items-center gap-2 md:gap-6 opacity-75 mt-3 md:mt-5">
           <div className="studio-line h-px w-6 md:w-20 bg-[#F2E9D8] origin-right"></div>
           <span className="studio-text font-black uppercase tracking-[0.4em] md:tracking-[0.8em] whitespace-nowrap text-[#F2E9D8]">Hand Crafted Studio</span>
           <div className="studio-line h-px w-6 md:w-20 bg-[#F2E9D8] origin-left"></div>
        </div>
      </div>

      {/* Floating Tagline — anchored to the bottom-left so it never overlaps the pizza.
          Width is constrained so the line doesn't stretch — text wraps naturally. */}
      <div className="hero-tagline-wrap absolute bottom-[4vh] left-[5vw] md:left-[3vw] z-30 max-w-[80vw] md:max-w-[40vw]">
        <p className="font-black text-[#E05A38] uppercase tracking-[0.4em] md:tracking-[0.5em] mb-1 md:mb-2 hero-eyebrow">Tallahassee Est. 2025</p>
        <p className="hero-tagline font-display font-black text-[#F2E9D8] uppercase leading-[0.95] tracking-tight">
          <span className="block">Sculpted by heat.</span>
          <span className="block">Defined by gravity.</span>
        </p>
      </div>
    </section>
  );
};

export default Hero;
